import React from 'react';
import { Line } from 'react-konva';
import { NodeComponent } from '../nodes/NodeComponent';
import { EdgeComponent } from '../edges/EdgeComponent';
import { GroupFrameComponent, ProxySocket } from '../nodes/GroupFrameComponent';
import { byContainment, byNestingDepth } from '../../utils/nesting';
import type {
  Node as DiagramNode,
  Edge,
  NodeGroup,
  Layer,
  Point,
  ThemePalette,
  IconStyle,
  SocketLabelVisibility,
  EdgeRoutingMode,
  Flow,
} from '../../types';
import type { ContextMenuState } from '../../types/app';
import type { InteractionPolicy } from '../../utils/interactionPolicy';
import type { JumperPoint, RoutedOrthoEdge } from '../../utils/edgeMath';
import type { AdjustedControlPoints } from '../../utils/forceEdgeLayout';
import { handleConnectToolNodeClick } from '../../utils/connectTools';
import { socketEdgeRoles } from '../../utils/connectionPolarity';

export interface FlowCanvasProps {
  // ── Group view state ──
  isInsideGroup: boolean;
  activeGroupId: string | null;

  // ── Diagram data ──
  groups: NodeGroup[];
  nodes: DiagramNode[];
  edges: Edge[];
  layers: Layer[];

  // ── Selection ──
  selectedItems: string[];

  // ── Proxy data ──
  proxySocketMap: Map<string, ProxySocket[]>;
  proxySocketLookup: Map<string, { groupId: string; proxy: ProxySocket }>;
  collapsedChildIds: Set<string>;

  // ── Group view filters ──
  groupViewEdgeIds: Set<string> | null;
  groupViewPrimaryIds: Set<string> | null;

  // ── Multi-drag positions ──
  dragOverridePos: Record<string, Point>;

  // ── Connection workflow ──
  connectionState: {
    isConnecting: boolean;
    sourceNodeId: string | null;
    sourceSocketId: string | null;
    tempEdge: { startX: number; startY: number; endX: number; endY: number } | null;
  };
  tempEdgePoints: number[] | null;

  // ── Display state ──
  viewport: { x: number; y: number; scale: number };
  palette: ThemePalette;

  // ── Interaction mode ──
  isViewOnly: boolean;
  interactionPolicy: InteractionPolicy;
  activeFlow: Flow | null;

  // ── Editing state ──
  inlineEdit: { nodeId: string; field: 'label' | 'description' } | null;
  inlineSocketEdit: { nodeId: string; socketId: string } | null;
  anchorNodeId: string | null;

  // ── Socket/waypoint state ──
  connectedSockets: Set<string>;
  waypointCandidateEdgeId: string | null;

  // ── Settings ──
  discoveryMode: boolean;
  iconStyle: IconStyle;
  socketLabelVisibility?: SocketLabelVisibility;
  defaultRoutingMode?: EdgeRoutingMode;
  /** The page's label multiplier, applied to every label the canvas draws. */
  textScale?: number;

  // ── Edge geometry ──
  jumperPoints: JumperPoint[];
  forceAdjustedCPs: AdjustedControlPoints;
  orthoMidShifts?: Record<string, number>;
  orthoPaths?: Record<string, RoutedOrthoEdge>;
  /** Per-edge destination-frame colour; absent for wires that stay home. */
  edgeTints?: Record<string, string>;

  // ── Info panel ──
  infoPanelNodeId: string | null;

  // ── Callbacks: Group frame ──
  selectItem: (id: string, shiftKey?: boolean) => void;
  handleGroupRename: (id: string) => void;
  setContextMenu: (state: ContextMenuState) => void;
  handleGroupDragMove: (id: string, dx: number, dy: number) => void;
  snapshotBeforeMutation: () => void;
  updateGroup: (id: string, updates: Partial<NodeGroup>) => void;
  enterGroup: (id: string) => void;

  // ── Callbacks: Edge ──
  handleEdgeContextMenu: (edgeId: string, x: number, y: number) => void;
  handleWaypointUpdate: (edgeId: string, waypoints: Point[]) => void;
  handleEdgeMessageUpdate: (edgeId: string, messages: any[]) => void;
  handleEdgeMessageDblClick: (edgeId: string, msgId: string, canvasCX: number, canvasCY: number, boxW: number, boxH: number) => void;
  inlineEdgeMsgId: string | null;
  setInlineEdgeEdit: (state: { edgeId: string; msgId?: string; canvasCX?: number; canvasCY?: number; canvasBoxW?: number; canvasBoxH?: number } | null) => void;

  // ── Callbacks: Node ──
  handleNodeUpdate: (id: string, updates: Partial<DiagramNode>) => void;
  handleSocketMouseDown: (nodeId: string, socketId: string) => void;
  handleSocketMouseUp: (nodeId: string, socketId: string) => void;
  handleSocketDoubleClick: (nodeId: string, socketId: string) => void;
  handleSocketSlideStart: (nodeId: string, socketId: string) => void;
  handleNodeRename: (id: string) => void;
  handleNodeContextMenu: (nodeId: string, x: number, y: number) => void;
  handleNodeResize: (id: string, width: number, height: number, options?: { fromCenter?: boolean; cascade?: boolean }) => void;
  setInlineEdit: (state: { nodeId: string; field: 'label' | 'description' } | null) => void;

  // ── Callbacks: Multi-drag ──
  handleMultiDragStart: (nodeId: string) => void;
  handleMultiDragMove: (nodeId: string, dx: number, dy: number) => void;
  handleMultiDragEnd: (nodeId: string, dx: number, dy: number) => void;

  // ── Callbacks: Waypoint drag ──
  handleWaypointDragMove: (nodeId: string, canvasX: number, canvasY: number) => void;
  handleWaypointDragEnd: (nodeId: string, canvasX: number, canvasY: number) => void;

  // ── Callbacks: Sidebar hover ──
  handleSidebarHoverStart: (kind: 'node' | 'participant' | 'message', id: string) => void;
  handleSidebarHoverEnd: (kind: 'node' | 'participant' | 'message') => void;

  // ── Callbacks: Visibility/opacity ──
  isEdgeVisible: (edge: Edge) => boolean;
  getNodeLayerOpacity: (node: { id?: string; layerId?: string }) => number;
  getEdgeOpacity: (edgeId: string) => number;
  isFlowEdgeAnimated: (edgeId: string) => boolean;

  // ── Callbacks: Info panel / color picker / navigation ──
  setInfoPanelNodeId: (id: string | null) => void;
  setColorPicker: (state: { show: boolean; targetNodeIds: string[]; avoidRect?: { x: number; y: number; w: number; h: number } } | null) => void;
  switchPageWithHistory: (pageId: string) => void;
  setLinkPageForNodeId: (id: string | null) => void;
  setIsPageManagerOpen: (open: boolean) => void;

  // ── Callbacks: Enter graph (subgraph) ──
  enterGraph: (nodeId: string) => void;

  // ── Ghost socket ──
  connectionSourceSocketType: 'input' | 'output' | null;
  onGhostSocketDrop: (nodeId: string, ghostType: 'input' | 'output') => void;
  onMagneticConnectStart?: (nodeId: string, localX: number, localY: number) => boolean | void;
  onMagneticConnectDrop?: (nodeId: string) => void;
  connectModifierHeldRef?: React.MutableRefObject<boolean>;
}

export const FlowCanvas: React.FC<FlowCanvasProps> = ({
  isInsideGroup,
  activeGroupId,
  groups,
  nodes,
  edges,
  layers,
  selectedItems,
  proxySocketMap,
  proxySocketLookup,
  collapsedChildIds,
  groupViewEdgeIds,
  groupViewPrimaryIds,
  dragOverridePos,
  connectionState,
  tempEdgePoints,
  viewport,
  palette,
  isViewOnly,
  interactionPolicy,
  activeFlow,
  inlineEdit,
  inlineSocketEdit,
  anchorNodeId,
  connectedSockets,
  waypointCandidateEdgeId,
  discoveryMode,
  iconStyle,
  socketLabelVisibility,
  defaultRoutingMode,
  textScale = 1,
  jumperPoints,
  forceAdjustedCPs,
  orthoMidShifts = {},
  orthoPaths = {},
  edgeTints = {},
  infoPanelNodeId,
  selectItem,
  handleGroupRename,
  setContextMenu,
  handleGroupDragMove,
  snapshotBeforeMutation,
  updateGroup,
  enterGroup,
  handleEdgeContextMenu,
  handleWaypointUpdate,
  handleEdgeMessageUpdate,
  handleEdgeMessageDblClick,
  inlineEdgeMsgId,
  setInlineEdgeEdit,
  handleNodeUpdate,
  handleSocketMouseDown,
  handleSocketMouseUp,
  handleSocketDoubleClick,
  handleSocketSlideStart,
  handleNodeRename,
  handleNodeContextMenu,
  handleNodeResize,
  setInlineEdit,
  handleMultiDragStart,
  handleMultiDragMove,
  handleMultiDragEnd,
  handleWaypointDragMove,
  handleWaypointDragEnd,
  handleSidebarHoverStart,
  handleSidebarHoverEnd,
  isEdgeVisible,
  getNodeLayerOpacity,
  getEdgeOpacity,
  isFlowEdgeAnimated,
  setInfoPanelNodeId,
  setColorPicker,
  switchPageWithHistory,
  setLinkPageForNodeId,
  setIsPageManagerOpen,
  enterGraph,
  connectionSourceSocketType,
  onGhostSocketDrop,
  onMagneticConnectStart,
  onMagneticConnectDrop,
  connectModifierHeldRef,
}) => {
  const socketRoles = socketEdgeRoles(edges);
  return (
    <>
      {/* Paint order is containment order: frames, then the nodes they hold,
         then every edge on top. A wire is the one thing on the canvas that
         has to stay visible wherever it runs - it is how the reader gets
         from one box to another - so nothing is allowed to cover it. */}
      {/* Group frames (behind nodes) — frames first, then regular groups.
         - On main canvas: all groups render (non-frame always COLLAPSED).
         - Inside a group view: only render direct child groups of the active group
           as collapsed proxies (for nested group support). Frames are hidden inside groups.
         - When using new subgraph model (graphNavStack), hide ALL frames and legacy groups. */}
      {(isInsideGroup
        ? (activeGroupId
            ? groups.filter(g => !g.isFrame && g.parentGroupId === activeGroupId)
            : []) // New subgraph model: no legacy groups/frames to render inside group view
        : [
            // Outermost frame first, so a frame nested inside another paints
            // over it however deep the nesting goes.
            ...groups.filter(g => g.isFrame).sort(byNestingDepth(groups, g => g.id)),
            ...groups.filter(g => {
              if (g.isFrame) return false;
              // Only top-level groups: no parent, or parent is a frame
              if (!g.parentGroupId) return true;
              const parent = groups.find(p => p.id === g.parentGroupId);
              return parent?.isFrame === true;
            }),
          ]
      ).map(group => (
        <GroupFrameComponent
          key={group.id}
          textScale={textScale}
          group={group.isFrame ? group : { ...group, collapsed: true }}
          isSelected={selectedItems.includes(group.id)}
          proxySockets={proxySocketMap.get(group.id) ?? []}
          onGroupClick={id => { selectItem(id); }}
          onGroupDoubleClick={id => { if (!isViewOnly) handleGroupRename(id); }}
          onGroupContextMenu={(id, x, y) => { if (!isViewOnly) setContextMenu({ type: 'group', x, y, groupId: id }); }}
          onDragMove={handleGroupDragMove}
          onResizeFrame={interactionPolicy.canMutateStructure
            ? (id, newPos, newSize) => { snapshotBeforeMutation(); updateGroup(id, { position: newPos, size: newSize }); }
            : undefined}
          onEnterGroup={group.isFrame ? undefined : id => enterGroup(id)}
          palette={palette}
          scale={viewport.scale}
          isFrame={group.isFrame}
          isReadOnly={isViewOnly}
        />
      ))}

      {/* Nodes */}
      {nodes
        .filter(node => {
          const layer = layers.find(l => l.id === (node.layerId ?? 'default'));
          if (!(layer?.visible ?? true)) return false;
          // Hide if inside a non-frame group (always collapsed on main canvas)
          // but only when NOT inside a group view
          if (!groupViewPrimaryIds && node.parentGroupId) {
            const grp = groups.find(g => g.id === node.parentGroupId);
            if (grp && !grp.isFrame) return false;
            if (grp?.isFrame && grp.collapsed) return false;
          }
          // Group view filter: show only nodes in the active group's childNodeIds
          if (groupViewPrimaryIds && !groupViewPrimaryIds.has(node.id)) return false;
          return true;
        })
        // Same cascade as the frames, extended to shapes drawn around other
        // shapes: whatever holds something else paints first, however deep it
        // goes, so nothing a container holds ends up underneath it.
        .sort(byContainment(nodes, groups))
        .map(node => (
          <NodeComponent
            key={node.id}
            textScale={textScale}
            node={node}
            onUpdate={handleNodeUpdate}
            connectedSockets={connectedSockets}
            socketRoles={socketRoles}
            isConnecting={connectionState.isConnecting}
            isSelected={selectedItems.includes(node.id)}
            isAnchor={node.id === anchorNodeId && !selectedItems.includes(node.id)}
            hideLabel={inlineEdit?.nodeId === node.id && inlineEdit?.field === 'label'}
            editingSocketId={inlineSocketEdit?.nodeId === node.id ? inlineSocketEdit.socketId : null}
            socketLabelVisibility={socketLabelVisibility}
            onSocketMouseDown={handleSocketMouseDown}
            onSocketMouseUp={handleSocketMouseUp}
            onSocketDoubleClick={handleSocketDoubleClick}
            onSocketSlideStart={handleSocketSlideStart}
            onNodeClick={(id, shiftKey) => {
              if (activeFlow) return;
              if (handleConnectToolNodeClick(id)) return;
              selectItem(id, shiftKey);
              if (infoPanelNodeId) setInfoPanelNodeId(id);
            }}
            onNodeDoubleClick={id => {
              if (activeFlow) return;
              const n = nodes.find(nd => nd.id === id);
              if (n?.childGraphId) enterGraph(id);
              else if (isViewOnly) return;
              else if (n?.type !== 'group_input' && n?.type !== 'group_output') setInfoPanelNodeId(id);
            }}
            onLabelDoubleClick={id => {
              if (isViewOnly) return;
              if (activeFlow) return;
              const n = nodes.find(nd => nd.id === id);
              if (n?.type !== 'group_input' && n?.type !== 'group_output') handleNodeRename(id);
            }}
            onDescDoubleClick={id => { if (!activeFlow && !isViewOnly) setInlineEdit({ nodeId: id, field: 'description' }); }}
            onNodeContextMenu={activeFlow || isViewOnly ? undefined : handleNodeContextMenu}
            onNodeResize={interactionPolicy.canMutateStyles ? handleNodeResize : undefined}
            onNodeHover={(id) => {
              if (id) handleSidebarHoverStart('node', id);
              else handleSidebarHoverEnd('node');
            }}
            layerOpacity={getNodeLayerOpacity(node)}
            iconStyle={iconStyle}
            palette={palette}
            onMultiDragStart={handleMultiDragStart}
            onMultiDragMove={handleMultiDragMove}
            onMultiDragEnd={handleMultiDragEnd}
            onWaypointDragMove={node.type === 'waypoint' ? handleWaypointDragMove : undefined}
            onWaypointDragEnd={node.type === 'waypoint' ? handleWaypointDragEnd : undefined}
            onActionInfo={id => {
              if (isViewOnly) return;
              const n = nodes.find(nd => nd.id === id);
              if (n?.type !== 'group_input' && n?.type !== 'group_output') setInfoPanelNodeId(id);
            }}
            onActionColor={id => {
              if (!interactionPolicy.canMutateStyles) return;
              const n = nodes.find(nd => nd.id === id);
              if (!n) return;
              const sx = n.position.x * viewport.scale + viewport.x;
              const sy = n.position.y * viewport.scale + viewport.y;
              setColorPicker({ show: true, targetNodeIds: [id], avoidRect: { x: sx, y: sy, w: n.size.width * viewport.scale, h: n.size.height * viewport.scale } });
            }}
            onActionEnterGroup={id => {
              const grp = groups.find(g => g.childNodeIds.includes(id) || g.id === id);
              if (grp) enterGroup(grp.id);
            }}
            onActionPortalNavigate={id => {
              const n = nodes.find(nd => nd.id === id);
              if (n?.linkedPageId) switchPageWithHistory(n.linkedPageId);
            }}
            onActionLinkPage={id => {
              if (!interactionPolicy.canMutateStructure) return;
              setLinkPageForNodeId(id);
              setIsPageManagerOpen(true);
            }}
            isReadOnly={isViewOnly}
            canDrag={interactionPolicy.canDragNodes}
            connectionSourceSocketType={connectionState.isConnecting ? connectionSourceSocketType : null}
            connectionSourceNodeId={connectionState.sourceNodeId}
            onGhostSocketDrop={onGhostSocketDrop}
            onMagneticConnectStart={onMagneticConnectStart}
            onMagneticConnectDrop={onMagneticConnectDrop}
            connectModifierHeldRef={connectModifierHeldRef}
          />
        ))}


      {/* Edges, drawn last so a box dragged across one cannot hide it — rerouted through proxy sockets when groups are collapsed.
         Inside a group view: only show edges where BOTH endpoints are in childNodeIds
         (boundary nodes are already in childNodeIds so their edges show naturally). */}
      {edges.filter(isEdgeVisible).filter(edge => {
        if (groupViewEdgeIds) {
          // Inside a group: show edges where both endpoints are in scope.
          // Uses expanded set that includes nested group boundary nodes.
          const srcIn = groupViewEdgeIds.has(edge.sourceNodeId);
          const tgtIn = groupViewEdgeIds.has(edge.targetNodeId);
          return srcIn && tgtIn;
        }
        // Main canvas: hide edges internal to a non-frame collapsed group
        const srcInCollapsed = collapsedChildIds.has(edge.sourceNodeId);
        const tgtInCollapsed = collapsedChildIds.has(edge.targetNodeId);
        if (srcInCollapsed && tgtInCollapsed) {
          const srcGroup = groups.find(g => !g.isFrame && g.childNodeIds.includes(edge.sourceNodeId));
          const tgtGroup = groups.find(g => !g.isFrame && g.childNodeIds.includes(edge.targetNodeId));
          if (srcGroup && tgtGroup && srcGroup.id === tgtGroup.id) return false;
        }
        return true;
      }).map(edge => {
        const srcProxy = proxySocketLookup.get(`${edge.sourceNodeId}:${edge.sourceSocketId}`);
        const tgtProxy = proxySocketLookup.get(`${edge.targetNodeId}:${edge.targetSocketId}`);

        let effectiveSrc: DiagramNode;
        let effectiveEdge = edge;

        if (srcProxy) {
          const grp = groups.find(g => g.id === srcProxy.groupId)!;
          effectiveSrc = {
            id: grp.id,
            type: 'group',
            position: grp.position,
            size: { width: 220, height: 100 },
            label: grp.label,
            sockets: [{ id: srcProxy.proxy.id, type: 'output' as const, dataType: 'data', position: srcProxy.proxy.position }],
          };
          effectiveEdge = { ...edge, sourceNodeId: grp.id, sourceSocketId: srcProxy.proxy.id };
        } else {
          const src = nodes.find(n => n.id === edge.sourceNodeId);
          if (!src) return null;
          const srcOverride = dragOverridePos[src.id];
          effectiveSrc = srcOverride ? { ...src, position: srcOverride } : src;
        }

        let effectiveTgt: DiagramNode;
        if (tgtProxy) {
          const grp = groups.find(g => g.id === tgtProxy.groupId)!;
          effectiveTgt = {
            id: grp.id,
            type: 'group',
            position: grp.position,
            size: { width: 220, height: 100 },
            label: grp.label,
            sockets: [{ id: tgtProxy.proxy.id, type: 'input' as const, dataType: 'data', position: tgtProxy.proxy.position }],
          };
          effectiveEdge = { ...effectiveEdge, targetNodeId: grp.id, targetSocketId: tgtProxy.proxy.id };
        } else {
          const tgt = nodes.find(n => n.id === edge.targetNodeId);
          if (!tgt) return null;
          const tgtOverride = dragOverridePos[tgt.id];
          effectiveTgt = tgtOverride ? { ...tgt, position: tgtOverride } : tgt;
        }

        return (
          <EdgeComponent
            key={edge.id}
            edge={effectiveEdge}
            sourceNode={effectiveSrc}
            targetNode={effectiveTgt}
            isSelected={selectedItems.includes(edge.id)}
            isWaypointCandidate={edge.id === waypointCandidateEdgeId}
            onEdgeClick={(id, shiftKey) => { if (!activeFlow) selectItem(id, shiftKey); }}
            onEdgeContextMenu={activeFlow || isViewOnly ? undefined : handleEdgeContextMenu}
            onWaypointUpdate={handleWaypointUpdate}
            onEdgeMessageUpdate={handleEdgeMessageUpdate}
            onEdgeMessageDblClick={activeFlow || isViewOnly ? undefined : handleEdgeMessageDblClick}
            editingMsgId={inlineEdgeMsgId ?? undefined}
            onEdgeDblClick={(edgeId) => { if (!activeFlow && !isViewOnly) setInlineEdgeEdit({ edgeId }); }}
            discoveryMode={discoveryMode}
            palette={palette}
            opacity={getEdgeOpacity(edge.id)}
            forceAnimated={isFlowEdgeAnimated(edge.id)}
            globalRoutingMode={defaultRoutingMode}
            textScale={textScale}
            allNodes={nodes}
            jumperPoints={jumperPoints}
            forceControlPoints={forceAdjustedCPs[edge.id] ?? null}
            orthoMidShift={orthoMidShifts[edge.id] ?? 0}
            routedPath={orthoPaths[edge.id]}
            tint={edgeTints[edge.id]}
            isReadOnly={isViewOnly}
          />
        );
      })}

      {/* Temp connection wire */}
      {tempEdgePoints && (
        <Line points={tempEdgePoints} stroke={palette.edgeSelected} strokeWidth={2}
          bezier dash={[8, 5]} listening={false} opacity={0.85} />
      )}


    </>
  );
};
